import { gql } from "@apollo/client";
import { InvoiceReviewStatus } from "common/graphql/fragments/invoiceFragment";
import { OrderBy } from "common/graphql/queryString";

export const GET_INVOICES = gql`
  query GetInvoices(
    $limit: Int!
    $offset: Int!
    $orderBy: [financial_invoice_order_by!]
    $filter: financial_invoice_bool_exp = { notice_sent_at: { _is_null: false } }
  ) {
    invoices: financial_invoice(where: $filter, limit: $limit, offset: $offset, order_by: $orderBy) {
      amountPaid: amount_paid
      adminFeeDue: admin_fee_due
      noticeSentAt: notice_sent_at
      dueAt: due_at
      voidedAt: voided_at
      id
      index
      dispute {
        id
        status
      }
      adminFeeAmountWaived: admin_fee_amount_waived
      registeredVehicle: registered_vehicle {
        id
        licensePlateNumber: license_plate
        licensePlateStateCode: state_code
        isRegistered: is_registered
      }
      totalAmountDue: total_amount_due
      transactionAmountDue: transaction_amount_due
      paymentTransaction: payment_transaction {
        id
        createdAt: created_at
      }
      payments {
        id
        paidAt: paid_at
      }
      replacedByInvoice: replaced_by_invoice {
        id
        index
      }
      updatedAt: updated_at
      reviewStatus: review_status
    }
    invoicesAggregate: financial_invoice_aggregate(where: $filter) {
      aggregate {
        count
        sum {
          amountPaid: amount_paid
        }
      }
    }
  }
`;

export interface GetInvoicesWhereFilter {
  notice_sent_at?: { _is_null?: boolean; _gte?: string; _lt?: string };
  index?: { _eq: number };
  registered_vehicle?: { license_plate: { _eq: string } };
  _not?: {
    _or: [
      {
        payment_transaction: Record<string, never>;
      },
      {
        amount_paid: { _gt: 0 };
      },
    ];
  };
  payment_transaction?: Record<string, never>;
  dispute?: { status: { _eq: "pending" } };
  amount_paid?: { _gt: 0 };
  voided_at?: { _is_null: boolean };
  due_at?: { _lt: string };
  created_at?: { _gte: string; _lt: string };
  review_status?: { _eq: InvoiceReviewStatus } | { _neq: InvoiceReviewStatus };
  _or?: InvoicePaidDateFilter[];
}

export interface PaidDateRange {
  _gte?: string;
  _lt?: string;
}

/** One way an invoice can have been paid within a date range; see buildPaidDateFilter. */
export type InvoicePaidDateFilter =
  | { payment_transaction: { created_at: PaidDateRange } }
  | { _and: InvoicePaidDateCondition[] };

export type InvoicePaidDateCondition =
  | { _not: { payment_transaction: Record<string, never> } }
  | { _not: { payments: { paid_at: PaidDateRange } } }
  | { payments: { paid_at: PaidDateRange } };
export interface GetInvoicesVars {
  limit: number;
  offset: number;
  orderBy?: InvoiceOrderBy[];
  filter?: GetInvoicesWhereFilter;
}

export type InvoiceOrderBy = InvoiceOrderByIndex | InvoiceOrderByNoticeDate | InvoiceOrderByPaidDate;

export interface InvoiceOrderByIndex {
  index: OrderBy;
}

export interface InvoiceOrderByNoticeDate {
  notice_sent_at: OrderBy;
}

export interface InvoiceOrderByPaidDate {
  payment_transaction: {
    created_at: OrderBy;
  };
}

// TODO: add computed field to enable proper ordering by total amount due

export interface GetInvoicesData {
  invoices: InvoiceSummary[];
  invoicesAggregate: {
    aggregate: {
      count: number;
      sum: {
        amountPaid: number;
      };
    };
  };
}

export interface InvoiceSummary {
  adminFeeDue: number;
  noticeSentAt: string | null;
  dueAt: string | null;
  voidedAt: string | null;
  dispute: {
    id: string;
    status: "approved" | "denied" | "pending";
  } | null;
  id: string;
  index: number;
  adminFeeAmountWaived: number;
  registeredVehicle: {
    id: string;
    licensePlateNumber: string;
    licensePlateStateCode: string;
    isRegistered: boolean;
  };
  totalAmountDue: number;
  transactionAmountDue: number;
  paymentTransaction: {
    id: string;
    createdAt: string;
  } | null;
  payments: InvoicePaymentSummary[];
  replacedByInvoice: {
    id: string;
    index: number;
  } | null;
  amountPaid: number;
  updatedAt: string;
  reviewStatus: InvoiceReviewStatus;
}

export interface InvoicePaymentSummary {
  id: string;
  paidAt: string;
}

export const GET_PENDING_INVOICE_REVIEW_COUNT = gql`
  query GetPendingInvoiceReviewCount {
    invoicesAggregate: financial_invoice_aggregate(where: { review_status: { _eq: pending } }) {
      aggregate {
        count
      }
    }
  }
`;

export interface GetPendingInvoiceReviewCountData {
  invoicesAggregate: {
    aggregate: {
      count: number;
    };
  };
}
